import { InlineAlert } from "@/components/layout-2/ds-settings";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { errorDetail, feedingClient } from "./api";
import { HistoryDialog, PreviewDialog, RunDetailDialog, RunsDialog } from "./detail-dialogs";
import { FeedingSettingsPage } from "./settings-page";
import type { FeedingPage, FeedingPageProps } from "./types";

/**
 * Content of the Feeding tab: the overview (`settings-page`), always, and the detail picked from
 * `subPath` (`preview`, `history`, `runs`, `runs/:id`) in a large dialog over it. The details stay
 * sub-routes of the tab (deep links, served `href`s, the browser's Back); closing one goes back to the
 * tab. The page lives here so a detail that changes it (a preview run) is read by the overview.
 */
export function FeedingTab({ page: served, subPath = "", onServiceChange }: { page: FeedingPage; subPath?: string; onServiceChange: () => void }) {
    const [page, setPage] = useState(served);
    useEffect(() => setPage(served), [served]);

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const path = pathname.replace(/\/+$/, "");
    const segments = subPath.split("/").filter(Boolean);
    const base = segments.length ? path.slice(0, path.length - segments.join("/").length - 1) : path;
    const code = page.campaign.code;

    const send: FeedingPageProps["send"] = async (request, success) => {
        try {
            setPage(await request);
            if (success) toast.success(success);
            return true;
        } catch (error) {
            toast.error(await errorDetail(error, "The change was refused."));
            return false;
        }
    };
    const changeService: FeedingPageProps["changeService"] = async action => {
        const done = await send(feedingClient(code, `${action}/`).post({}).json<FeedingPage>(), action === "activate" ? "Campaign put in service" : "Campaign paused");
        if (done) onServiceChange();
        return done;
    };
    const refresh = () => {
        feedingClient(code)
            .get()
            .json<FeedingPage>()
            .then(setPage)
            .catch(() => undefined);
    };
    const props: FeedingPageProps = { page, base, send, changeService, refresh };
    const close = () => navigate(base);

    const [first, second] = segments;
    // Filters and exclusions are edited in their step dialogs; the former sub-routes still land there.
    if (segments.length === 1 && (first === "filters" || first === "exclusions")) return <Navigate to={`${base}?${first}=edit`} replace />;
    const known = segments.length === 0 || (segments.length === 1 && ["preview", "history", "runs"].includes(first)) || (segments.length === 2 && first === "runs");
    return (
        <>
            <div className="flex flex-col gap-4">
                {!known && <InlineAlert type="error" title="This page does not exist" />}
                <FeedingSettingsPage {...props} />
            </div>
            {first === "preview" && segments.length === 1 && <PreviewDialog {...props} onClose={close} />}
            {first === "history" && segments.length === 1 && <HistoryDialog {...props} onClose={close} />}
            {first === "runs" && segments.length === 1 && <RunsDialog {...props} onClose={close} />}
            {first === "runs" && segments.length === 2 && <RunDetailDialog key={second} {...props} runId={second} onClose={close} />}
        </>
    );
}
